import React, { useEffect, useState } from 'react';
import './search-statement-dialog.css';
import Tooltip from '../../../lib/tooltip/Tooltip';
import {
	ITabManager,
	useSearchStatementContext,
	withNestedSearchStatementProvider,
} from './search-statement-context';
import { EditorService } from '../../../services/editor';
import DataAggregationSection from './DataAggregationSection';
import FilterConditionsSection from './FilterConditionsSection';
import OutputFormatSection from './OutputFormatSection';
import ResultsSection from './ResultsSection';
import Button from '../../../lib/button/Button';
import MainSourceAndInputsSection from './MainSourceAndInputsSection';
import { searchStatementState as searchStatementStateLib } from '@elyx-code/project-logic-tree';
import { popupNotification } from '../../../popup-notification';
import { Logger } from '@elyx-code/common-ts-utils';

const { SearchStatementState } = searchStatementStateLib;

type SearchStatementState = searchStatementStateLib.SearchStatementState;

export interface INestedSearchStatementBuilderProps {
	project: EditorService;
	parent: SearchStatementState,
	query: SearchStatementState,
	ready: boolean;
	tabManager: ITabManager;
	// recalculateParent: () => void;
	onClose?: (e: any) => void;
}

const NestedSearchStatementBuilder: React.FC<
	INestedSearchStatementBuilderProps
> = ({ onClose, query, parent }) => {
	const { project, entity, searchStatementState } = useSearchStatementContext();

	Logger.log('query: ', query);
	Logger.log('parent: ', parent);

	const useForceUpdate = () => {
		const [_, setValue] = useState(0);
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useEffect(() => {
		project?.on('value-written', ({ owner, value: _ }: any = {}) => {
			if ((entity?.inputs || []).includes(owner)) {
				forceUpdate();
			}
		});

		project?.on('value-removed', ({ owner }: any = {}) => {
			if ((entity?.inputs || []).includes(owner)) {
				forceUpdate();
			}
		});
	}, [entity]);

	if (!project) {
		return null;
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				gap: '4px',
				justifyContent: 'space-between',
			}}
		>
			<span
				style={{
					maxWidth: '100%',
					maxHeight: '100%',
					overflowY: 'scroll',
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
				}}
			>
				<MainSourceAndInputsSection hideInputs />

				<hr
					style={{ margin: '20px 0 0 0', border: '1px solid rgba(0,0,0,0.08)' }}
				/>

				<DataAggregationSection />

				<hr
					style={{ margin: '1px 0', border: '1px solid rgba(0,0,0,0.08)' }}
				/>

				<FilterConditionsSection />

				<hr
					style={{ margin: '1px 0', border: '1px solid rgba(0,0,0,0.08)' }}
				/>

				<OutputFormatSection />

				<hr
					style={{ margin: '1px 0', border: '1px solid rgba(0,0,0,0.08)' }}
				/>

				<ResultsSection />
			</span>

			<div
				style={{
					alignSelf: 'flex-start',
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-end',
					width: '100%',
					gap: '16px',
					padding: '0 40px',
				}}
			>
				<Button
					id='search-statement-dialog-save-query-button'
					onClick={(e: any) => {
						const queryString = searchStatementState.toQuery();

						navigator.clipboard.writeText(queryString);

						popupNotification({
							text: 'The query has been copied to the clipboard',
						});
					}}
					color='primary'
					variant='outlined'
				>
					Copy as SQL
				</Button>
				<Tooltip
					anchorQuerySelector='#search-statement-dialog-save-query-button'
					placement='bottom'
					openDelay={300}
				>
					Copy as a native SQL query
				</Tooltip>
				<Button
					id='search-statement-dialog-done-button'
					onClick={(e: any) => {
						onClose?.(e);
					}}
					color='primary'
				>
					Done
				</Button>
				<Tooltip
					anchorQuerySelector='#search-statement-dialog-done-button'
					placement='bottom'
					openDelay={300}
				>
					Save the changes and return to the parent search statement
				</Tooltip>
			</div>
		</div>
	);
};

export default withNestedSearchStatementProvider<INestedSearchStatementBuilderProps>(
	NestedSearchStatementBuilder
);
